import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';

// Live API functions
const api = {
    getHsns: async (page = 1, limit = 10, search = '') => {
        const endpoint = search
            ? `/gst/hsn/search`
            : `/gst/hsn`;
        
        const params = search
            ? { code: search }
            : { page, limit };

        const response = await apiWithAuth.get(endpoint, { params });
        const data = response.data;

        // Normalize response: search returns an array, paginated returns an object
        return Array.isArray(data)
            ? { data, total: data.length, page: 1, limit: data.length }
            : data;
    },

    createHsn: async (data) => {
        const response = await apiWithAuth.post('/gst/hsn', data);
        return response.data;
    },

    updateHsn: async (id, data) => {
        const response = await apiWithAuth.put(`/gst/hsn/${id}`, data);
        return response.data;
    },

    deleteHsn: async (id) => {
        const response = await apiWithAuth.delete(`/gst/hsn/${id}`);
        return response.data;
    },
};

const HSNManagementPage = () => {
    const permissions = usePermissions('HSN Codes');
    const [hsns, setHsns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
    const [searchTerm, setSearchTerm] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentHsn, setCurrentHsn] = useState(null);

    const fetchHsns = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.getHsns(pagination.page, pagination.limit, searchTerm);
            setHsns(result.data);
            setPagination(prev => ({ ...prev, total: result.total }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, searchTerm]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchHsns();
        }, 300); // Debounce search
        return () => clearTimeout(timer);
    }, [fetchHsns]);

    const handleOpenModal = (hsn = null) => {
        setCurrentHsn(hsn);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentHsn(null);
    };

    const handleSave = async (formData) => {
        try {
            if (currentHsn) {
                await api.updateHsn(currentHsn._id, formData);
            } else {
                await api.createHsn(formData);
            }
            handleCloseModal();
            fetchHsns(); // Refresh data
        } catch (err) {
            console.error('Failed to save HSN:', err);
            alert(`Error: ${err.message}`); // Show error to the user
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this HSN code?')) {
            try {
                await api.deleteHsn(id);
                fetchHsns(); // Refresh data
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }

    const totalPages = searchTerm ? 1 : Math.ceil(pagination.total / pagination.limit);

    return (
        <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="mx-auto">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">HSN Management</h1>
                        <div className="flex items-center space-x-4 w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by code..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handlePageChange(1); // Reset to page 1 on search
                                    }}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <button
                                onClick={() => handleOpenModal()}
                                className="flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                Add HSN
                            </button>
                        </div>
                    </header>

                    {loading && <p className="text-center text-gray-500 py-8">Loading...</p>}
                    {error && <p className="text-center text-red-500 py-8">{error}</p>}

                    {!loading && !error && (
                        <>
                            <HSNTable hsns={hsns} onEdit={handleOpenModal} onDelete={handleDelete} />
                            <Pagination currentPage={pagination.page} totalPages={totalPages} onPageChange={handlePageChange} disabled={!!searchTerm} />
                        </>
                    )}

                </div>
            </div>
            {isModalOpen && <HSNFormModal hsn={currentHsn} onSave={handleSave} onClose={handleCloseModal} />}
        </div>
    );
};

// HSN Table Component (No changes needed)
const HSNTable = ({ hsns, onEdit, onDelete }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
                <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HSN Code (4-Digit)</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HSN Code (8-Digit)</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">GST Rate (%)</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
            </thead>
            <tbody className="text-gray-700">
                {hsns.map((hsn) => (
                    <tr key={hsn._id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4 cursor-default">{hsn.hsnCode4}</td>
                        <td className="py-3 px-4 cursor-default">{hsn.hsnCode8}</td>
                        <td className="py-3 px-4 cursor-default max-w-sm truncate relative group cursor-default">
                            <span className="block truncate" title={hsn.description}>
                                {hsn.description}
                            </span>
                            <div className="absolute z-10 hidden group-hover:flex bg-white text-gray-900 border border-gray-300 rounded-md p-2 text-sm shadow-lg w-64 top-full left-0 mt-1 pointer-events-none">
                                {hsn.description}
                            </div>
                        </td>
                        <td className="py-3 px-4 cursor-default">{hsn.gstRate}</td>
                        <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                                <button onClick={() => onEdit(hsn)} className="text-blue-500 hover:text-blue-700 transition duration-150"><Edit size={18} /></button>
                                <button onClick={() => onDelete(hsn._id)} className="text-red-500 hover:text-red-700 transition duration-150"><Trash2 size={18} /></button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        {hsns.length === 0 && <p className="text-center text-gray-500 py-8">No HSN codes found.</p>}
    </div>
);

// Form Modal Component (No changes needed)
const HSNFormModal = ({ hsn, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        hsnCode4: hsn?.hsnCode4 || '',
        hsnCode8: hsn?.hsnCode8 || '',
        description: hsn?.description || '',
        gstRate: hsn?.gstRate || '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">{hsn ? 'Edit HSN' : 'Create HSN'}</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="hsnCode4" className="block text-sm font-medium text-gray-700">HSN Code (4-Digit)</label>
                                <input type="text" name="hsnCode4" id="hsnCode4" value={formData.hsnCode4} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label htmlFor="hsnCode8" className="block text-sm font-medium text-gray-700">HSN Code (8-Digit)</label>
                                <input type="text" name="hsnCode8" id="hsnCode8" value={formData.hsnCode8} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                                <input type="text" name="description" id="description" value={formData.description} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                            <div>
                                <label htmlFor="gstRate" className="block text-sm font-medium text-gray-700">GST Rate (%)</label>
                                <input type="number" name="gstRate" id="gstRate" value={formData.gstRate} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Pagination Component (Added a 'disabled' prop)
const Pagination = ({ currentPage, totalPages, onPageChange, disabled }) => {
    if (totalPages <= 1 || disabled) return null;

    return (
        <div className="flex justify-between items-center mt-6">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronLeft className="h-5 w-5 mr-2" />
                Previous
            </button>
            <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </span>
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Next
                <ChevronRight className="h-5 w-5 ml-2" />
            </button>
        </div>
    );
};


export default HSNManagementPage;